import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Music2, BookOpenText, Loader2, AlertTriangle, Sliders,
  Headphones, Copy, ExternalLink, Sparkles, Disc3, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch (e) { void e; return null; }
}

function AmbientBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 opacity-60">
      <div className="absolute -left-40 top-0 h-[460px] w-[460px] rounded-full bg-fuchsia-600/[0.22] blur-3xl" />
      <div className="absolute right-[-220px] top-40 h-[540px] w-[540px] rounded-full bg-sky-500/[0.18] blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-emerald-500/[0.10] blur-3xl" />
    </div>
  );
}

function BrandRow() {
  return (
    <Link to="/" data-testid="session-viewer-home-link" className="group flex items-center gap-3 transition hover:opacity-90">
      <img
        src="/brand/elpm-logo.png"
        alt="ELPM Studio Pro"
        data-testid="brand-wordmark-share-session"
        className="h-9 w-auto select-none drop-shadow-[0_2px_14px_rgba(232,121,249,0.22)]"
        draggable="false"
      />
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 transition group-hover:text-fuchsia-200">
        ← Back to studio
      </span>
    </Link>
  );
}

function LoadingState() {
  return (
    <div className="grid place-items-center py-24 text-center">
      <Loader2 className="mb-3 h-6 w-6 animate-spin text-fuchsia-300" />
      <p className="font-mono text-[11px] uppercase tracking-wider text-white/45">Loading shared session…</p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="grid place-items-center py-20 text-center">
      <AlertTriangle className="mb-3 h-7 w-7 text-amber-300" />
      <p data-testid="session-viewer-error" className="mb-3 font-display text-lg font-semibold text-white/85">{message}</p>
      <Link to="/">
        <Button data-testid="session-viewer-back-btn" className="rounded-2xl bg-gradient-to-r from-fuchsia-500 to-sky-500 text-white hover:opacity-90">
          <Music2 className="mr-2 h-4 w-4" />Open the studio instead
        </Button>
      </Link>
    </div>
  );
}

/** Eight-bar faux EQ visualizer — purely decorative. Stops animating when paused. */
function FauxVisualizer({ playing }) {
  return (
    <div className="flex h-20 items-end gap-1.5 rounded-2xl border border-white/[0.07] bg-black/30 p-3"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -1px 0 rgba(0,0,0,0.45)" }}>
      {Array.from({ length: 24 }).map((_, i) => (
        <span
          key={`viz-${i}`}
          className="flex-1 rounded-full bg-gradient-to-t from-fuchsia-500 via-rose-400 to-amber-300"
          style={{
            height: `${playing ? 20 + Math.random() * 75 : 18 + (i % 6) * 6}%`,
            opacity: playing ? 0.95 : 0.45,
            transition: "height 280ms ease-out, opacity 280ms",
          }}
        />
      ))}
    </div>
  );
}

function CopyBar({ shareUrl }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.message("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Couldn't copy", { description: e?.message });
    }
  };
  return (
    <div className="flex items-stretch gap-2">
      <input
        readOnly
        data-testid="session-viewer-share-url"
        value={shareUrl}
        onFocus={(e) => e.target.select()}
        className="flex-1 rounded-xl border border-white/10 bg-black/40 px-3 py-2 font-mono text-[11px] text-white/85"
      />
      <button
        type="button"
        data-testid="session-viewer-copy-btn"
        onClick={onCopy}
        className={`flex items-center gap-1.5 rounded-xl px-3 py-2 font-mono text-[10px] uppercase tracking-wider transition ${
          copied
            ? "bg-emerald-500/30 text-emerald-100 ring-1 ring-emerald-400/50"
            : "bg-white/[0.06] text-white/85 hover:bg-white/[0.12]"
        }`}
      >
        {copied ? <><CheckCircle2 className="h-3 w-3" />Copied</> : <><Copy className="h-3 w-3" />Copy</>}
      </button>
    </div>
  );
}

/**
 * SessionViewer — the public page at `/session/{share_id}`. Renders the
 * mastered session WAV with playback + lyrics + a sidebar that lists every
 * channel in the original mix so visitors get a sense of how the song was
 * arranged.
 */
export default function SessionViewer() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const playPostedRef = useRef(false);

  const shareUrl = useMemo(() => (typeof window !== "undefined" ? `${window.location.origin}/session/${id}` : ""), [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/session/share/${id}`)
      .then((res) => { if (!cancelled) setSession(res.data); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setError(status === 404 ? "This session link doesn't exist or was removed." : (e.message || "Failed to load this session"));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const onAudioPlay = () => {
    setPlaying(true);
    if (playPostedRef.current) return;
    playPostedRef.current = true;
    axios.post(`${API}/session/share/${id}/play`).catch(() => { /* analytics-only */ });
  };
  const onAudioPause = () => setPlaying(false);
  const onAudioEnded = () => setPlaying(false);

  const durLabel = fmtDuration(session?.duration);
  const ageLabel = session?.created_at ? timeAgo(session.created_at) : null;
  // Backend returns a relative URL — prepend the API origin so the audio
  // request goes through the FastAPI ingress.
  const audioSrc = session?.audio_url
    ? (session.audio_url.startsWith("http") ? session.audio_url : `${BACKEND}${session.audio_url}`)
    : null;

  return (
    <div className="min-h-screen overflow-x-hidden font-body text-white">
      <title>{session?.title ? `${session.title} · ELPM Studio Pro` : "ELPM Studio Pro"}</title>
      <meta name="description" content={`A shared multi-track session from ELPM Studio Pro · ${session?.tempo || 120} BPM · ${session?.musical_key || "C Major"}`} />
      <meta property="og:title" content={session?.title || "ELPM Studio Pro Session"} />
      <meta property="og:description" content={`Listen to "${session?.title || 'Untitled session'}" — a multi-track session made with ELPM Studio Pro.`} />
      <meta property="og:type" content="music.song" />
      {audioSrc && <meta property="og:audio" content={audioSrc} />}

      <AmbientBackdrop />

      <div className="relative z-10 mx-auto max-w-5xl p-4 md:p-8">
        <header className="mb-8 flex items-center justify-between gap-4">
          <BrandRow />
          <div className="hidden items-center gap-2 sm:flex">
            <Link
              to="/explore"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="session-viewer-explore-link"
              className="rounded-2xl border border-sky-400/30 bg-sky-500/10 px-4 py-2 text-sm font-semibold text-sky-100 transition hover:bg-sky-500/20"
            >
              Explore →
            </Link>
            <Link
              to="/"
              data-testid="session-viewer-open-studio-cta"
              className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/[0.12]"
            >
              Open the studio →
            </Link>
          </div>
        </header>

        {loading && <LoadingState />}
        {!loading && error && <ErrorState message={error} />}

        {!loading && session && (
          <motion.main
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="session-viewer-main"
            className="grid gap-6 lg:grid-cols-[1fr_320px]"
          >
            <section className="space-y-5">
              <div className="glass-card overflow-hidden rounded-3xl p-6">
                <div className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-fuchsia-300">
                  <Sparkles className="h-3.5 w-3.5" />Multi · track · session
                </div>
                <h1 data-testid="session-viewer-title" className="font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                  {session.title || "Untitled session"}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-white/55">
                  {session.owner_alias && <span data-testid="session-viewer-owner">by {session.owner_alias}</span>}
                  <span data-testid="session-viewer-tempo">· {session.tempo} BPM</span>
                  <span data-testid="session-viewer-key">· {session.musical_key}</span>
                  {durLabel && <span>· {durLabel}</span>}
                  {ageLabel && <span>· {ageLabel}</span>}
                  <span data-testid="session-viewer-plays" className="ml-auto rounded-full bg-white/[0.06] px-2 py-0.5">
                    {Number(session.plays || 0).toLocaleString()} play{session.plays === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="mt-5">
                  <FauxVisualizer playing={playing} />
                </div>

                <audio
                  ref={audioRef}
                  data-testid="session-viewer-audio"
                  src={audioSrc}
                  controls
                  preload="metadata"
                  onPlay={onAudioPlay}
                  onPause={onAudioPause}
                  onEnded={onAudioEnded}
                  className="mt-4 w-full"
                />

                <div className="mt-4">
                  <CopyBar shareUrl={shareUrl} />
                </div>
              </div>

              {session.lyrics?.trim() && (
                <div className="glass-card rounded-3xl p-6">
                  <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-fuchsia-300">
                    <BookOpenText className="h-3.5 w-3.5" />Lyrics
                  </div>
                  <pre data-testid="session-viewer-lyrics" className="whitespace-pre-wrap break-words font-body text-[15px] leading-relaxed text-white/85">
                    {session.lyrics}
                  </pre>
                </div>
              )}
            </section>

            <aside className="space-y-4">
              {Array.isArray(session.channels) && session.channels.length > 0 && (
                <div className="glass-card rounded-3xl p-5">
                  <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-white/55">
                    <Sliders className="h-3.5 w-3.5" />Tracks · in · this · mix
                  </div>
                  <ul data-testid="session-viewer-channels" className="space-y-1">
                    {session.channels.map((c) => (
                      <li
                        key={`ch-${c.idx}`}
                        data-testid={`session-viewer-channel-${c.idx}`}
                        className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-black/30 px-2.5 py-1.5"
                      >
                        <span className="font-mono text-[10px] uppercase tracking-wider text-white/45">
                          CH {String(c.idx + 1).padStart(2, "0")}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12px] text-white/85">
                          {(c.name || `Track ${c.idx + 1}`).replace(/\.[^.]+$/, "")}
                        </span>
                        {fmtDuration(c.duration) && (
                          <span className="font-mono text-[10px] tabular-nums text-white/45">{fmtDuration(c.duration)}</span>
                        )}
                        {c.muted && <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-white/45">M</span>}
                        {c.soloed && <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-100">S</span>}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 font-mono text-[9px] uppercase tracking-wider text-white/40">
                    Audio above is the bounced master · individual stems aren't shared.
                  </p>
                </div>
              )}

              <div className="rounded-3xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/15 via-rose-500/10 to-amber-400/10 p-5 backdrop-blur-xl">
                <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-fuchsia-200">
                  <Headphones className="h-3.5 w-3.5" />Make · your · own
                </div>
                <p className="text-sm leading-6 text-white/85">
                  Record, clone voices, generate songs with AI, and arrange 64-channel mixes — all in your browser.
                </p>
                <Link
                  to="/"
                  data-testid="session-viewer-cta"
                  className="mt-3 inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-fuchsia-500 to-sky-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  <Music2 className="mr-2 h-4 w-4" />Open the studio
                  <ExternalLink className="ml-2 h-3.5 w-3.5 opacity-70" />
                </Link>
              </div>
            </aside>
          </motion.main>
        )}

        <footer className="mt-10 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-white/45">
          <span className="flex items-center gap-2"><Disc3 className="h-3.5 w-3.5" />ELPM · Studio · Pro</span>
          <Link to="/" className="hover:text-white/70">Open studio →</Link>
        </footer>
      </div>
    </div>
  );
}
